import { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from '@artstorefronts/ui';
import { Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

import AdminLayout from '../../../components/AdminLayout';
import { useAuth } from '../../../context/auth';

const SEARCH_USERS = gql`
  query PointsAdminUserSearch($search: String) {
    users(search: $search, limit: 8) {
      users {
        asfId
        firstName
        lastName
        email
      }
    }
  }
`;

const USER_POINTS = gql`
  query AdminUserPoints($userId: ID!) {
    adminUserPoints(userId: $userId) {
      balance {
        balance
        lifetimePoints
        spendablePoints
      }
      ledger {
        id
        delta
        spendableDelta
        reason
        detail
        note
        adminUserId
        createdAt
      }
    }
  }
`;

const ADJUST = gql`
  mutation StaffPointsCorrection($userId: ID!, $amount: Int!, $note: String!, $showNoteToMember: Boolean) {
    adminAdjustPoints(userId: $userId, amount: $amount, note: $note, showNoteToMember: $showNoteToMember) {
      balance {
        balance
        lifetimePoints
        spendablePoints
      }
      ledger {
        id
        delta
        spendableDelta
        reason
        detail
        note
        adminUserId
        createdAt
      }
    }
  }
`;

type FoundUser = { asfId: string; firstName: string | null; lastName: string | null; email: string };

type LedgerEntry = {
  id: string;
  delta: number;
  spendableDelta: number | null;
  reason: string;
  detail: string | null;
  note: string | null;
  adminUserId: string | null;
  createdAt: string;
};

type UserPoints = {
  balance: { balance: number; lifetimePoints: number; spendablePoints: number };
  ledger: LedgerEntry[];
};

function userLabel(user: FoundUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email;
}

function lookupInputValue(selected: FoundUser | null, term: string): string {
  if (!selected) return term;
  const label = userLabel(selected);
  return label === selected.email ? selected.email : `${label} — ${selected.email}`;
}

/**
 * The number a row actually moved. A spend carries `delta: 0` on purpose — its magnitude lives
 * in `spendableDelta` — so reading `delta` alone would print every redemption as 0.
 */
function displayDelta(entry: LedgerEntry): number {
  return entry.delta !== 0 ? entry.delta : (entry.spendableDelta ?? 0);
}

export default function AdminPointsPage() {
  const { user } = useAuth();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<FoundUser | null>(null);
  const [open, setOpen] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [showNoteToMember, setShowNoteToMember] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(term.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [term]);

  const [runSearch, { data: searchData, loading: searching }] = useLazyQuery<{ users: { users: FoundUser[] } }>(
    SEARCH_USERS,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    if (debounced.length >= 2 && !selected) {
      void runSearch({ variables: { search: debounced } });
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [debounced, selected, runSearch]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const { data, loading, refetch } = useQuery<{ adminUserPoints: UserPoints }>(USER_POINTS, {
    variables: { userId: selected?.asfId ?? '' },
    skip: !selected,
    fetchPolicy: 'network-only',
  });

  const [adjust, { loading: saving }] = useMutation(ADJUST);

  const matches = useMemo(() => searchData?.users.users ?? [], [searchData]);
  const points = data?.adminUserPoints ?? null;
  const parsedAmount = Number(amount);
  const amountValid = Number.isInteger(parsedAmount) && parsedAmount !== 0;
  // The server refuses this too. The button is disabled so a self-target cannot toast success.
  const adjustingSelf = selected != null && user?.user_id != null && String(user.user_id) === String(selected.asfId);

  /**
   * Clear the form whenever the dialog closes, not just after a success.
   *
   * A refused submit (self-adjustment, a zero or fractional amount, a blank reason) leaves the
   * dialog open with its values intact — including "show this reason to the member". Without this, cancelling and
   * reopening for a different member inherits that tick, and an internal note like "suspected
   * vote ring" would be published to them by accident.
   */
  const closeAdjust = () => {
    setAdjusting(false);
    setAmount('');
    setNote('');
    setShowNoteToMember(false);
  };

  const submit = async () => {
    if (!selected || !amountValid || !note.trim()) return;
    if (adjustingSelf) {
      toast.error('You cannot adjust your own points');
      return;
    }
    try {
      await adjust({
        variables: { userId: selected.asfId, amount: parsedAmount, note: note.trim(), showNoteToMember },
      });
      toast.success(`${parsedAmount > 0 ? 'Added' : 'Removed'} ${Math.abs(parsedAmount).toLocaleString()} points`);
      closeAdjust();
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Adjustment failed');
    }
  };

  return (
    <AdminLayout title="Points" description="Look up a member, see their points, and correct them.">
      <div className="mb-8 max-w-xl" ref={boxRef}>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search by name or email…"
            value={lookupInputValue(selected, term)}
            onChange={(e) => {
              setSelected(null);
              setTerm(e.target.value);
            }}
          />
          {selected && (
            <button
              type="button"
              aria-label="Clear selected member"
              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => {
                setSelected(null);
                setTerm('');
              }}
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        {open && (
          <div className="mt-1 rounded-lg border bg-white shadow-sm">
            {searching && (
              <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
                <Loader2 className="size-4 animate-spin" /> Searching…
              </div>
            )}
            {!searching && matches.length === 0 && <div className="p-3 text-sm text-gray-500">No members found.</div>}
            {matches.map((match) => (
              <button
                key={match.asfId}
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                onClick={() => {
                  setSelected(match);
                  setOpen(false);
                }}
              >
                <span className="font-medium">{userLabel(match)}</span>
                <span className="text-gray-500"> — {match.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {!selected && <p className="text-sm text-gray-500">Search for a member to see their points.</p>}

      {selected && loading && !points && (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin" />
        </div>
      )}

      {selected && points && (
        <>
          <section className="mb-8 flex flex-wrap items-end gap-8">
            <div>
              <div className="text-xs tracking-wide text-gray-500 uppercase">Spendable</div>
              <div className="text-3xl font-medium">{points.balance.spendablePoints.toLocaleString()}</div>
            </div>
            <div>
              {/* Reputation is what the public leaderboard ranks on — spending never moves it,
                  but a staff adjustment does. Both are shown so that is visible before acting. */}
              <div className="text-xs tracking-wide text-gray-500 uppercase">Reputation</div>
              <div className="text-3xl font-medium">{points.balance.balance.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs tracking-wide text-gray-500 uppercase">All time</div>
              <div className="text-3xl font-medium">{points.balance.lifetimePoints.toLocaleString()}</div>
            </div>
            <Button onClick={() => setAdjusting(true)} disabled={adjustingSelf}>
              Adjust points
            </Button>
            {adjustingSelf ? <p className="w-full text-sm text-gray-500">You cannot adjust your own points.</p> : null}
          </section>

          <section className="overflow-x-auto">
            <h2 className="mb-2 text-base font-medium">Activity</h2>
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-4">Change</th>
                  <th className="py-2 pr-4">Reason</th>
                  <th className="py-2 pr-4">Detail</th>
                  <th className="py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {points.ledger.map((entry) => (
                  <tr key={entry.id} className="border-b">
                    <td className={`py-2 pr-4 font-medium ${displayDelta(entry) < 0 ? 'text-red-600' : ''}`}>
                      {displayDelta(entry) > 0 ? '+' : ''}
                      {displayDelta(entry).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">{entry.reason}</td>
                    <td className="py-2 pr-4">
                      {/* `note` is the staff reason, shown here whether or not the member sees
                          it; `detail` is what the member reads. */}
                      {entry.note ?? entry.detail ?? '—'}
                      {entry.adminUserId && <div className="text-xs text-gray-500">by admin {entry.adminUserId}</div>}
                    </td>
                    <td className="py-2 text-gray-500">{new Date(entry.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {points.ledger.length === 0 && <p className="py-6 text-sm text-gray-500">No points activity yet.</p>}
          </section>
        </>
      )}

      <Dialog open={adjusting} onOpenChange={(next) => !next && closeAdjust()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust points for {selected ? userLabel(selected) : ''}</DialogTitle>
            <DialogDescription>
              A positive number adds, a negative number removes. This moves their reputation and their place on the
              leaderboard, not just what they can spend.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <Input
              placeholder="Amount, e.g. 500 or -250"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-label="Adjustment amount"
            />
            <Textarea
              className="min-h-[88px]"
              placeholder="Why? Always recorded."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              aria-label="Adjustment reason"
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showNoteToMember}
                onChange={(e) => setShowNoteToMember(e.target.checked)}
              />
              Show this reason to the member
            </label>
            <p className="text-xs text-gray-500">
              Off, their activity log says only that the ArtHelper team made a change.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="min-h-[44px] flex-1 sm:flex-none"
              onClick={closeAdjust}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="min-h-[44px] flex-1 sm:flex-none"
              onClick={submit}
              disabled={saving || !amountValid || !note.trim()}
            >
              {saving ? 'Saving…' : 'Apply adjustment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
